export const TABLE_SEPARATOR_REGEX = /^\|[\s\-:|]+\|$/;
export const INLINE_CODE_REGEX = /^`([^`]+)`/;
export const BOLD_REGEX = /^(\*\*|__)([^*_]+?)\1/;

const isTableSeparator = (line: string): boolean => {
  return TABLE_SEPARATOR_REGEX.test(line);
};

const parseTableRow = (line: string): string[] => {
  return line
    .split("|")
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0);
};

const isValidTableRow = (line: string): boolean => {
  return line.startsWith("|") && line.endsWith("|");
};

const processTableLine = (
  line: string,
  tableRows: string[][],
  hasSeparator: boolean
): { hasSeparator: boolean; shouldBreak: boolean } => {
  if (isTableSeparator(line)) {
    return { hasSeparator: true, shouldBreak: false };
  }

  if (!isValidTableRow(line)) {
    const hasTableData = tableRows.length > 0 && hasSeparator;
    return { hasSeparator, shouldBreak: hasTableData };
  }

  const cells = parseTableRow(line);
  if (cells.length > 0) {
    tableRows.push(cells);
  }

  return { hasSeparator, shouldBreak: false };
};

export const parseMarkdownTable = (text: string): string[][] | null => {
  const lines = text
    .trim()
    .split("\n")
    .filter((line) => line.trim());
  if (lines.length < 2) return null;

  const tableRows: string[][] = [];
  let hasSeparator = false;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    const result = processTableLine(trimmedLine, tableRows, hasSeparator);
    hasSeparator = result.hasSeparator;
    if (result.shouldBreak) break;
  }

  if (tableRows.length < 2 || !hasSeparator) return null;

  return tableRows;
};

export type MarkdownToken =
  | { type: "text"; content: string }
  | { type: "bold"; content: string }
  | { type: "italic"; content: string }
  | { type: "code"; content: string };

export const checkItalicChar = (
  text: string,
  currentIndex: number,
  char: string
) => {
  const pos = text.indexOf(char, currentIndex);
  if (pos === -1 || pos !== currentIndex) return null;

  const nextChar = text[pos + 1];
  const prevChar = text[pos - 1];
  if (nextChar === char || prevChar === char) return null;

  const closePos = text.indexOf(char, pos + 1);
  if (closePos === -1) return null;

  const afterClose = text[closePos + 1];
  if (afterClose === char) return null;

  const content = text.slice(pos + 1, closePos);
  if (!content) return null;

  return { content, length: closePos - pos + 1 };
};

export const findNextTokenIndex = (
  text: string,
  currentIndex: number
): number => {
  let nextTokenIndex = text.length;
  const codePos = text.indexOf("`", currentIndex);
  const boldPos1 = text.indexOf("**", currentIndex);
  const boldPos2 = text.indexOf("__", currentIndex);
  const italicPos1 = text.indexOf("*", currentIndex);
  const italicPos2 = text.indexOf("_", currentIndex);

  if (codePos !== -1 && codePos < nextTokenIndex) nextTokenIndex = codePos;
  if (boldPos1 !== -1 && boldPos1 < nextTokenIndex) nextTokenIndex = boldPos1;
  if (boldPos2 !== -1 && boldPos2 < nextTokenIndex) nextTokenIndex = boldPos2;
  if (italicPos1 !== -1 && italicPos1 < nextTokenIndex)
    nextTokenIndex = italicPos1;
  if (italicPos2 !== -1 && italicPos2 < nextTokenIndex)
    nextTokenIndex = italicPos2;

  return nextTokenIndex;
};
